// theme
import Theme from "../theme/ThemeProvider.styled";
import { filters } from "../components/map/Maps.config";

const daylist = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// check if the value passed in is todays date, returns true or false
const isTodaysDate = day => {
	const currentDate = new Date();
	const currentDay = daylist[currentDate.getDay()];

	return day.replace(/\s+/g, "") === currentDay;
};

// format date object, used for comparing times
// returns the number of milliseconds since January 1, 1970, 00:00:00 UTC, so it can be used to compare dates
const formatDateTime = time => {
	return Date.parse(`01/01/2020 ${time}:00`);
};

// passed in a string provided from the API in this format:
// "Mon 07:00-22:00; Tue 07:00-22:00; Wed 07:00-22:00; Thur 07:00-22:00; Fri 07:00-22:00; Sat 07:00-22:00; Sun 07:00-22:00
// formats the string and checks todays time frame to see if the store is open, closed or closing soon
const getOpeningHourState = times => {
	if (!times) {
		return null;
	}

	// break up the string supplied
	const openingTimesSuppled = times.split(";");

	// get the current day and time to compare
	const currentDate = new Date();
	const currentDay = daylist[currentDate.getDay()];
	const currentHour = currentDate.getHours();
	const currentMins = currentDate.getMinutes();

	// hardcoded date used solely to compare times
	const currentTime = Date.parse(`01/01/2020 ${currentHour}:${currentMins}:00`);

	// find the opening hours for today
	let openingTimes = openingTimesSuppled.filter(times => {
		return times.includes(currentDay);
	})[0];

	if (!openingTimes) {
		return {
			status: "closed",
			statusText: "Closed"
		};
	} else {
		openingTimes = openingTimes.replace(/\s+/g, "");

		// if for exmaple it is Saturday and the post office is closed
		if (openingTimes.includes("Closed")) {
			return {
				status: "closed",
				statusText: "Closed"
			};
		}
		// remove pre-pended day
		openingTimes = openingTimes.substr(openingTimes.search(/\d/), openingTimes.length);
	}

	// check the times are outside opening hours
	const openingTime = openingTimes.substr(0, openingTimes.indexOf("-"));
	const closingTime = openingTimes.substr(openingTimes.length - 5, 5);

	// if the current time is less than the opening hours or is greater than the closing hours
	if (currentTime < formatDateTime(openingTime) || currentTime > formatDateTime(closingTime)) {
		return {
			status: "closed",
			statusText: "Closed"
		};
	}

	// check for closed at lunch status
	if (openingTimes.length > 16) {
		const lunchtimeStart = openingTimes.substr(openingTimes.indexOf("-") + 1, 5);
		const lunchtimeEnd = openingTimes.substr(openingTimes.indexOf("-") + 6, 5);

		if (currentTime > formatDateTime(lunchtimeStart) && currentTime < formatDateTime(lunchtimeEnd)) {
			return {
				status: "closed",
				statusText: "Closed for lunch"
			};
		}
	}

	// if the current time less than an hour away
	if (Date.parse(`01/01/2020 ${currentHour + 1}:${currentMins}:00`) > Date.parse(`01/01/2020 ${closingTime}:00`)) {
		return {
			status: "closing-soon",
			statusText: "Closing soon"
		};
	}

	// return open
	return {
		status: "open",
		statusText: "Open now"
	};
};

const getRadiusFromZoom = (initialZoom, currentZoom, initialDistanceAtInitialZoom) => {
	// formula to get the radius at the current zooom
	return Math.round(Math.pow(2, initialZoom - currentZoom) * initialDistanceAtInitialZoom);
};

// returning zoom level based on the radius passed in
const radiusToZoom = radius => {
	if (radius === 10) {
		return 12;
	}

	if (radius === 15) {
		return 11.5;
	}

	if (radius === 20) {
		return 11;
	}

	// default zoom
	return 13;
};

// Capitalise the first letter of each word supplied
const capitaliseLetters = str => {
	// if there is no string or it's just white space
	if (!str || !str.replace(/\s/g, "").length) return "";

	// exception for GPO
	if (str === "GPO") return str;

	str = str.trim().toLowerCase().split(" ");

	// capitalise the first letter of the each word
	for (let i = 0, x = str.length; i < x; i++) {
		const letterToCapitalise = str[i][0];

		if (letterToCapitalise) {
			str[i] = letterToCapitalise.toUpperCase() + str[i].substr(1);
		}
	}

	return str.join(" ");
};

// check if google maps API is already loaded
const mapAlreadyLoaded = () => {
	if (window.google && window.google.maps) {
		return true;
	}
	return false;
};

// a location.type property is passed in and it then returns the correct marker
const getMarkerIcon = location => {
	if (location.Type) {
		if (["dsu", "dso", "super dso"].includes(location.Type.toLowerCase())) {
			return Theme.lightBluePin;
		}

		if (location.Type.toLowerCase() === "parcel locker") {
			return Theme.bluePin;
		}

		if (location.Type.toLowerCase() === "postpoint") {
			return Theme.limePin;
		}
	}

	return Theme.greenPin;
};

// make sure that the users location is within the bounds of Ireland
const inBounds = (point, bounds) => {
	if (mapAlreadyLoaded()) {
		const boundaries = new window.google.maps.LatLngBounds(
			new window.google.maps.LatLng(bounds.latLngBounds.south, bounds.latLngBounds.west),
			new window.google.maps.LatLng(bounds.latLngBounds.north, bounds.latLngBounds.east)
		);
		return boundaries.contains(point);
	}
	return false;
};

const zoomPrevent = event => {
	if (event.keyCode == 61 || event.keyCode == 107 || event.keyCode == 173 || event.keyCode == 109 || event.keyCode == 187 || event.keyCode == 189) {
		event.preventDefault();
	}
	// 107 Num Key  +
	// 109 Num Key  -
	// 173 Min Key  hyphen/underscor Hey
	// 61 Plus key  +/= key
};

// preventing zooom as there is already zoom on the map
// prevent the body from scolling under the overlay
const preventZoomAndScroll = () => {
	document.addEventListener("keydown", zoomPrevent, false);
	document.body.style.overflowY = "hidden";
};

// only preventing zoom when app is open
// allow the body from scoll under the overlay
const allowZooomAndScroll = () => {
	document.removeEventListener("keydown", zoomPrevent, false);
	document.body.style.overflowY = "auto";
};

const getMarkerIconObject = location => {
	return {
		url: getMarkerIcon(location),
		scaledSize: new window.google.maps.Size(30, 30),
		origin: new window.google.maps.Point(0, 0),
		anchor: new window.google.maps.Point(20, 40)
	};
};

const initDataLayer = () => {
	window.dataLayer = window.dataLayer || [];
};

/**
 *
 * @param {*} obj
 * reusable function to push data to GTM
 * expects an object
 */
const pushDataLayer = obj => {
	window.dataLayer.push(obj);
};

/**
 *
 * @param {*} obj
 * reusable function to push virtualPageView event to GA
 * pageviewUrl : string
 * pageviewTitle : string
 */
const virtualPageView = (pageviewUrl, pageviewTitle) => {
	const GTMData = {
		event: "virtualPageView",
		pageview_url: pageviewUrl,
		pageview_title: pageviewTitle
	};
	pushDataLayer(GTMData);
};

/**
 *
 * @param {*} obj
 * reusable function to push virtualPageView event to GA
 * pageviewUrl : string
 * pageviewTitle : string
 * filter: when preloading a page there is a filter
 */
const virtualPageViewWithFilter = (pageviewUrl, pageviewTitle, filters) => {
	const GTMData = {
		event: "virtualPageView",
		pageview_url: pageviewUrl,
		pageview_title: pageviewTitle,
		Pre_applied_filters: filters
	};
	pushDataLayer(GTMData);
};

/**
 *
 * @param {*} obj
 * reusable function to push postLocatorFilters event to GA
 * filterCategory : string
 * filter : string
 * preloaded: bool
 */
const postLocatorFilterEvent = (filterCategory, filter, preloaded) => {
	const GTMData = {
		event: "postLocatorFilters",
		filter_category: filterCategory,
		filter: filter,
		pre_loaded: preloaded ? "Yes" : "No"
	};

	pushDataLayer(GTMData);
};

/**
 * handle for different types of urls
 *
 * 1. Normal example
 * 		#storeloactor
 * 2. with one type
 * 		#storeloactor+POST-OFFICE
 * 3. with multiple types
 * 		#storeloactor+POST-OFFICE|PARCEL-LOCKER|POSTPOINT|Delivery-Office
 * 4. with types and a pre-selected service (can have only one preselected service)
 * 		#storeloactor+POST-FFICE|PARCEL-LOCKER,ADDRESSPAL
 *
 */

const getUrlSettings = hash => {
	const mapSettings = {};
	let tempString = hash;

	// strip out the start of the url
	tempString = tempString.substr(tempString.indexOf("+") + 1, tempString.length);

	// if the url doesn't contain a , it has a service. Extract the service
	if (tempString.includes(",")) {
		// if there is a service
		const service = tempString.substr(tempString.indexOf(",") + 1, hash.length);

		// set the service
		mapSettings.service = service.replace(/-/g, " ");

		// remove the service string from the url
		tempString = tempString.substr(0, tempString.indexOf(","));
	}

	// Get the types from the url
	if (hash.includes("+")) {
		mapSettings.types = tempString;
	}

	return mapSettings;
};

// get current filter from the preddefined filters list
const getCurrentFilter = selectedFilter => {
	return filters.filter(item => {
		return item.type === selectedFilter.replace(/-/g, " ");
	})[0];
};

/**
 * Debouncing scroll will only call the event handler after the event
 * has stopped firing for a certain amount of time. This will ensure that your
 * function will only be called once the scroll event is “complete.”
 */

const debounce = (fn, time) => {
	let timeout;

	return () => {
		const functionCall = () => fn.apply(this);

		clearTimeout(timeout);
		timeout = setTimeout(functionCall, time);
	};
};

export {
	getOpeningHourState,
	capitaliseLetters,
	isTodaysDate,
	getRadiusFromZoom,
	getMarkerIcon,
	virtualPageView,
	initDataLayer,
	pushDataLayer,
	postLocatorFilterEvent,
	mapAlreadyLoaded,
	radiusToZoom,
	getMarkerIconObject,
	preventZoomAndScroll,
	allowZooomAndScroll,
	formatDateTime,
	inBounds,
	getUrlSettings,
	virtualPageViewWithFilter,
	getCurrentFilter,
	debounce
};
